import { useEffect, useState } from "react";
import type { PublicTripType, PublicTripTypeCatalog } from "@ichegou/contracts";

/** The site's ride and delivery types, as staff set them in admin (`GET /public/trip-types`). */
export type TripType = PublicTripType;

const BASE = (import.meta.env?.VITE_API_URL as string | undefined) ?? "/api";

/** Empty when the API can't answer: the page goes on without the section rather than naming stale types. */
export async function fetchTripTypes(signal?: AbortSignal): Promise<TripType[]> {
  try {
    const res = await fetch(`${BASE}/public/trip-types`, { signal, credentials: "omit", headers: { accept: "application/json" } });
    return res.ok ? ((await res.json()) as PublicTripTypeCatalog).types : [];
  } catch {
    return [];
  }
}

/** Null while loading. A page handed its types (tests) doesn't ask. */
export function useTripTypes(given?: TripType[]) {
  const [types, setTypes] = useState<TripType[] | null>(given ?? null);
  useEffect(() => {
    if (given) return;
    const stop = new AbortController();
    void fetchTripTypes(stop.signal).then((found) => {
      if (!stop.signal.aborted) setTypes(found);
    });
    return () => stop.abort();
  }, [given]);
  return types;
}

/** The presets with 3D art here (tools/brand/trip_type_icons_3d.py); the others draw as their vehicle. */
const ART = new Set(["car", "car_comfort", "car_premium", "moto", "women", "package", "package_moto"]);

/** The picture for a type's preset: what the site shows until, or instead of, an uploaded icon. */
export const presetArt = (t: Pick<TripType, "icon" | "vehicleKind">) =>
  `/trip-types/${ART.has(t.icon) ? t.icon : t.vehicleKind === "motorcycle" ? "moto" : "car"}.webp`;

/** "até 30 kg", "até 2,5 kg". */
export const weightText = (kg: number) => `até ${kg.toLocaleString("pt-BR")} kg`;

/** What a delivery type carries: its hint, else its weight, else nothing. */
export const packageText = (t: Pick<TripType, "packageHint" | "maxWeightKg">) => t.packageHint ?? (t.maxWeightKg === null ? null : weightText(t.maxWeightKg));
